import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useCreatePurchaseOrderGuardStore from "../store/createPurchaseOrderGuardStore";

/**
 * Warns before leaving create PO with items in cart (sidebar, back link, etc.).
 */
export function useCartNavigationBlocker(cartHasItems) {
  const navigate = useNavigate();
  const setCartHasItems = useCreatePurchaseOrderGuardStore(
    (s) => s.setCartHasItems,
  );
  const leavePromptOpen = useCreatePurchaseOrderGuardStore(
    (s) => s.leavePromptOpen,
  );
  const requestLeave = useCreatePurchaseOrderGuardStore((s) => s.requestLeave);
  const storeConfirmLeave = useCreatePurchaseOrderGuardStore(
    (s) => s.confirmLeave,
  );
  const storeCancelLeave = useCreatePurchaseOrderGuardStore(
    (s) => s.cancelLeave,
  );
  const allowNextNavigation = useCreatePurchaseOrderGuardStore(
    (s) => s.allowNextNavigation,
  );

  useEffect(() => {
    setCartHasItems(cartHasItems);
    return () => setCartHasItems(false);
  }, [cartHasItems, setCartHasItems]);

  useEffect(() => {
    if (!cartHasItems) return undefined;
    const onBeforeUnload = (event) => {
      const { skipGuard } = useCreatePurchaseOrderGuardStore.getState();
      if (skipGuard) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [cartHasItems]);

  const tryNavigate = useCallback(
    (path, options) => {
      const { allowed } = requestLeave(path);
      if (allowed) navigate(path, options);
    },
    [navigate, requestLeave],
  );

  const confirmLeave = useCallback(() => {
    const path = storeConfirmLeave();
    if (path) navigate(path);
  }, [navigate, storeConfirmLeave]);

  const cancelLeave = useCallback(() => {
    storeCancelLeave();
  }, [storeCancelLeave]);

  return {
    showLeaveDialog: leavePromptOpen,
    confirmLeave,
    cancelLeave,
    allowNavigation: allowNextNavigation,
    tryNavigate,
  };
}
